import { useState } from "react";
import type { CoveragePartyInput } from "@/lib/coverage/coverageLookup";
import { saveCoverageSaleIssue } from "@/lib/coverage/coverageClient";
import type { FadeMode, IssueFilter, SaleDot, SaleIssue } from "@/lib/coverage/coverageSales";

type Props = {
  sales: SaleDot[];
  issues: SaleIssue[];
  parties: CoveragePartyInput[];
  fade: FadeMode;
  issueFilter: IssueFilter;
  onFade: (next: FadeMode) => void;
  onFilter: (next: IssueFilter) => void;
  onSaved: () => void;
};

const field = "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

export function CoverageSalesPanel(props: Props) {
  const [saleId, setSaleId] = useState("");
  const [kind, setKind] = useState<SaleIssue["kind"]>("delivery");
  const [partyId, setPartyId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    const res = await saveCoverageSaleIssue({
      sale_fact_id: saleId,
      kind,
      party_id: partyId,
      note,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setNote("");
    props.onSaved();
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Sales this year</h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">{props.sales.length} dots. Amber dots fade by the choice below.</p>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <button type="button" className={chip(props.fade === "age")} onClick={() => props.onFade("age")}>Fade by age</button>
        <button type="button" className={chip(props.fade === "dollars")} onClick={() => props.onFade("dollars")}>Fade by dollars</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <button type="button" className={chip(props.issueFilter === "all")} onClick={() => props.onFilter("all")}>All sales</button>
        <button type="button" className={chip(props.issueFilter === "delivery")} onClick={() => props.onFilter("delivery")}>Delivery</button>
        <button type="button" className={chip(props.issueFilter === "installation")} onClick={() => props.onFilter("installation")}>Install</button>
        <button type="button" className={chip(props.issueFilter === "customer")} onClick={() => props.onFilter("customer")}>Customer</button>
      </div>
      <div className="mt-3 space-y-2">
        <label className="block text-xs text-gray-500">
          Sale
          <select className={field} value={saleId} onChange={(e) => setSaleId(e.target.value)}>
            <option value="">Choose a sale</option>
            {props.sales.slice(0, 80).map((sale) => (
              <option key={sale.id} value={sale.id}>{sale.label}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-500">
          Problem
          <select className={field} value={kind} onChange={(e) => setKind(e.target.value === "installation" || e.target.value === "customer" ? e.target.value : "delivery")}>
            <option value="delivery">Delivery</option>
            <option value="installation">Install</option>
            <option value="customer">Customer</option>
          </select>
        </label>
        <label className="block text-xs text-gray-500">
          Plant, if this problem belongs to one
          <select className={field} value={partyId} onChange={(e) => setPartyId(e.target.value)}>
            <option value="">Any plant whose territory contains the sale</option>
            {props.parties.filter((party) => party.active).map((party) => (
              <option key={party.id} value={party.id}>{party.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-500">
          Note
          <input className={field} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <button
          type="button"
          disabled={busy || !saleId || !note.trim()}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 disabled:opacity-50"
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Tag this sale"}
        </button>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>
    </section>
  );
}

function chip(on: boolean): string {
  return on
    ? "rounded-full bg-gray-900 px-2 py-1 text-white dark:bg-zinc-100 dark:text-zinc-900"
    : "rounded-full border border-gray-300 px-2 py-1 dark:border-zinc-600";
}
